import { useEffect, useState, useCallback } from "react";
import { api, fmtMoney } from "../../../lib/api";
import { Button } from "../../ui/button";
import { Textarea } from "../../ui/textarea";
import { Checkbox } from "../../ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import { FileText, Download, Trash2, ShieldAlert, Sparkles } from "lucide-react";
import { toast } from "sonner";

const MODES = [{ v: "blind", label: "Blind profile (anonymous)" }, { v: "full", label: "Full memorandum (post-NDA)" }];
const lines = (arr) => (arr || []).join("\n");
const unlines = (s) => s.split("\n").map((x) => x.trim()).filter(Boolean);

async function errorText(err) {
    const d = err.response?.data;
    if (d instanceof Blob) { try { return JSON.parse(await d.text()).detail; } catch { return "Export failed"; } }
    return typeof d?.detail === "string" ? d.detail : err.message;
}

function Editor({ draftId, onDeleted, onUpdated }) {
    const [draft, setDraft] = useState(null);
    const [form, setForm] = useState({ overview: "", highlights: "", growth: "" });
    const [saving, setSaving] = useState(false);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        const { data } = await api.get(`/im/${draftId}`);
        setDraft(data);
        const n = data.content.narrative;
        setForm({ overview: n.business_overview || "", highlights: lines(n.investment_highlights), growth: lines(n.growth_opportunities) });
    }, [draftId]);
    useEffect(() => { load(); }, [load]);
    if (!draft) return <div className="text-sm text-muted-foreground p-4">Loading…</div>;

    const c = draft.content, fin = c.financials, ccy = fin.currency, blind = draft.mode === "blind";
    const save = async () => {
        setSaving(true);
        try {
            const { data } = await api.patch(`/im/${draftId}`, {
                business_overview: form.overview, investment_highlights: unlines(form.highlights), growth_opportunities: unlines(form.growth) });
            setDraft(data); toast.success("Saved"); onUpdated?.();
        } catch (err) { toast.error(typeof err.response?.data?.detail === "string" ? err.response.data.detail : err.message); }
        finally { setSaving(false); }
    };
    const download = async () => {
        setBusy(true);
        try {
            const resp = await api.get(`/im/${draftId}/pdf`, { responseType: "blob" });
            const name = /filename="([^"]+)"/.exec(resp.headers["content-disposition"] || "")?.[1] || "memorandum.pdf";
            const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
            const a = document.createElement("a"); a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
        } catch (err) { toast.error(await errorText(err)); }
        finally { setBusy(false); }
    };
    const del = async () => {
        if (!confirm("Delete this draft?")) return;
        await api.delete(`/im/${draftId}`); onDeleted();
    };

    return (
        <div className="border border-border rounded-md bg-white p-5 space-y-4" data-testid="im-editor">
            <div className="flex items-start justify-between">
                <div>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{blind ? "Blind profile" : "Full memorandum"} · v{draft.version}</div>
                    <div className="font-display font-bold text-lg">{c.title}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        {draft.ai_generated && !draft.edited ? <><Sparkles className="w-3 h-3" /> AI-drafted narrative — review before sending</> : draft.edited ? "Edited by you" : "Template narrative — edit below"}
                    </div>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={download} disabled={busy || draft.warnings?.length > 0} data-testid="im-pdf-btn"><Download className="w-3.5 h-3.5 mr-1.5" /> PDF</Button>
                    <Button variant="ghost" size="icon" onClick={del}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
                </div>
            </div>

            {draft.warnings?.length > 0 && (
                <div className="flex gap-2 items-start text-sm border border-red-200 bg-red-50 text-red-800 rounded-md p-3" data-testid="im-warning">
                    <ShieldAlert className="w-4 h-4 mt-0.5 shrink-0" />
                    <div>This blind profile contains identifying details: <b>{draft.warnings.join(", ")}</b>. Export is blocked until they're removed — or regenerate the draft.</div>
                </div>
            )}

            <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-3">
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Business overview</label>
                    <Textarea rows={5} value={form.overview} onChange={(e) => setForm((f) => ({ ...f, overview: e.target.value }))} data-testid="im-overview" />
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Investment highlights (one per line)</label>
                    <Textarea rows={5} value={form.highlights} onChange={(e) => setForm((f) => ({ ...f, highlights: e.target.value }))} />
                    <label className="text-xs uppercase tracking-wider text-muted-foreground">Growth opportunities (one per line)</label>
                    <Textarea rows={4} value={form.growth} onChange={(e) => setForm((f) => ({ ...f, growth: e.target.value }))} />
                    <p className="text-xs text-muted-foreground">Keep figures out of the narrative — they come from your data and appear in the tables.</p>
                    <Button onClick={save} disabled={saving} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="im-save-btn">{saving ? "Saving…" : "Save narrative"}</Button>
                </div>
                <div className="space-y-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">Financial summary {fin.rounded && "(rounded)"} — snapshot, from your data</div>
                    <table className="w-full text-sm"><tbody>
                        {fin.headline.map((h) => <tr key={h.label} className="border-b border-border/60"><td className="py-1.5">{h.label}</td><td className="py-1.5 text-right font-mono">{fmtMoney(h.value, ccy)}</td></tr>)}
                        {fin.normalised_margin_pct != null && <tr><td className="py-1.5">Normalised EBITDA margin</td><td className="py-1.5 text-right font-mono">{fin.normalised_margin_pct}%</td></tr>}
                    </tbody></table>
                    {fin.periods.length > 0 && <div className="text-xs text-muted-foreground">{fin.periods.length} trading period{fin.periods.length === 1 ? "" : "s"} included.</div>}
                    {c.dd_status && <div className="text-xs text-muted-foreground">Data-room status appendix included ({c.dd_status.provided}/{c.dd_status.applicable} provided).</div>}
                    <p className="text-xs text-muted-foreground">Financials are frozen when a draft is created. Generate a new version to pick up changes.</p>
                </div>
            </div>
        </div>
    );
}

export default function MemorandumPanel({ dealId }) {
    const [drafts, setDrafts] = useState([]);
    const [mode, setMode] = useState("blind");
    const [includeDd, setIncludeDd] = useState(false);
    const [useAi, setUseAi] = useState(true);
    const [creating, setCreating] = useState(false);
    const [openId, setOpenId] = useState(null);

    const load = useCallback(async () => { const { data } = await api.get(`/im/deal/${dealId}`); setDrafts(data); return data; }, [dealId]);
    useEffect(() => { load().then((d) => d[0] && setOpenId((cur) => cur || d[0].id)); }, [load]);

    const create = async () => {
        setCreating(true);
        try {
            const { data } = await api.post(`/im/deal/${dealId}`, { mode, use_ai: useAi, include_dd_status: mode === "full" && includeDd });
            await load(); setOpenId(data.id);
            if (data.warnings?.length) toast.warning("The draft contains identifying details — see the warning");
        } catch (err) { toast.error(err.response?.data?.detail || err.message); }
        finally { setCreating(false); }
    };

    return (
        <div className="space-y-4" data-testid="dd-memorandum">
            <div>
                <h3 className="font-display font-bold tracking-tight flex items-center gap-2"><FileText className="w-4 h-4 text-gold" /> Investment memorandum</h3>
                <p className="text-xs text-muted-foreground mt-0.5 max-w-2xl">
                    A blind profile for first contact (codename, rounded figures, no location or add-back labels) or a full memorandum for buyers under NDA.
                    Figures are generated from your deal data, never by AI. Anomalies are never included.
                </p>
            </div>
            <div className="flex flex-wrap items-end gap-3 border border-border rounded-md bg-white p-4">
                <div className="w-64">
                    <Select value={mode} onValueChange={setMode}>
                        <SelectTrigger data-testid="im-mode-select"><SelectValue /></SelectTrigger>
                        <SelectContent>{MODES.map((m) => <SelectItem key={m.v} value={m.v}>{m.label}</SelectItem>)}</SelectContent>
                    </Select>
                </div>
                <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={useAi} onCheckedChange={(v) => setUseAi(!!v)} /> Draft narrative with AI</label>
                {mode === "full" && <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={includeDd} onCheckedChange={(v) => setIncludeDd(!!v)} /> Include data-room status</label>}
                <Button onClick={create} disabled={creating} className="bg-foreground text-background hover:bg-neutral-800 hover:text-white" data-testid="im-create-btn">{creating ? "Generating…" : "Generate draft"}</Button>
            </div>

            {drafts.length > 0 && (
                <div className="flex flex-wrap gap-2">
                    {drafts.map((d) => (
                        <button key={d.id} onClick={() => setOpenId(d.id)} className={`px-3 py-1.5 rounded-md text-xs border ${openId === d.id ? "bg-foreground text-background" : "bg-white hover:bg-neutral-50"}`}>
                            {d.mode === "blind" ? "Blind" : "Full"} v{d.version} · {new Date(d.created_at).toLocaleDateString("en-GB")}
                        </button>
                    ))}
                </div>
            )}
            {openId && drafts.some((d) => d.id === openId) && (
                <Editor key={openId} draftId={openId} onUpdated={load} onDeleted={async () => { const d = await load(); setOpenId(d[0]?.id || null); }} />
            )}
        </div>
    );
}
